import React from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, Shield, Mail, Phone, MapPin } from 'lucide-react';
import { SEO } from './SEO';

const PrivacyPage: React.FC = () => {
  const { t } = useTranslation(['privacy', 'seo']);
  const isGlobal = typeof window !== 'undefined' && (window as any).__isIndianLocale__ === false;

  return (
    <div className="min-h-screen bg-white text-gray-800 py-12 px-8">
      <SEO
        title={t('seo:privacy.title', { defaultValue: 'Privacy Policy | Sarvalok Data Protection' })}
        description={t('seo:privacy.description', { defaultValue: 'Learn how Sarvalok protects your privacy, handles personal data, and ensures secure access to cultural knowledge and spiritual research resources.' })}
        url="/privacy-policy"
      />
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <h1 className="text-4xl md:text-5xl font-extrabold text-center mb-8 text-black">
          {t('privacy.title')}
        </h1>
        <p className="text-center text-lg mb-12 italic">
          {t('privacy.effectiveDate')}
        </p>

        {/* Intro Section */}
        <section className="mb-12">
          <p className="text-lg leading-relaxed">
            {t('privacy.introduction')}
          </p>
        </section>

        {/* Sections */}
        <section className="space-y-12">
          {/* 1. Personal Data We Collect */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black flex items-center gap-2 mb-4">
              <Lock size={28} /> {t('privacy.dataCollection.title')}
            </h2>
            <p className="mb-4">
              {t('privacy.dataCollection.description')}
            </p>
            <p className="mb-4 font-semibold">
              {t('privacy.dataCollection.personalDataProvided.title')}
            </p>
            <p className="mb-4">
              {t('privacy.dataCollection.personalDataProvided.description')}
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong className="font-semibold">{t('privacy.dataCollection.personalDataProvided.accountInfo.title')}</strong>{' '}
                {t('privacy.dataCollection.personalDataProvided.accountInfo.description')}
              </li>
              <li>
                <strong className="font-semibold">{t('privacy.dataCollection.personalDataProvided.userContent.title')}</strong> {t('privacy.dataCollection.personalDataProvided.userContent.description')}
              </li>
              <li>
                <strong className="font-semibold">{t('privacy.dataCollection.personalDataProvided.communicationInfo.title')}</strong>{' '}
                {t('privacy.dataCollection.personalDataProvided.communicationInfo.description')}
              </li>
              <li>
                <strong className="font-semibold">{t('privacy.dataCollection.personalDataProvided.otherInfo.title')}</strong>{' '}
                {t('privacy.dataCollection.personalDataProvided.otherInfo.description')}
              </li>
            </ul>

            <p className="mb-4 font-semibold">
              {t('privacy.dataCollection.technicalData.title')}
            </p>
            <p className="mb-4">
              {t('privacy.dataCollection.technicalData.description')}
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong className="font-semibold">{t('privacy.dataCollection.technicalData.logData.title')}</strong> {t('privacy.dataCollection.technicalData.logData.description')}
              </li>
              <li>
                <strong className="font-semibold">{t('privacy.dataCollection.technicalData.usageData.title')}</strong> {t('privacy.dataCollection.technicalData.usageData.description')}
              </li>
              <li>
                <strong className="font-semibold">{t('privacy.dataCollection.technicalData.deviceInfo.title')}</strong> {t('privacy.dataCollection.technicalData.deviceInfo.description')}
              </li>
              <li>
                <strong className="font-semibold">{t('privacy.dataCollection.technicalData.locationInfo.title')}</strong>{' '}
                {t('privacy.dataCollection.technicalData.locationInfo.description')}
              </li>
              <li>
                <strong className="font-semibold">{t('privacy.dataCollection.technicalData.cookies.title')}</strong>{' '}
                {t('privacy.dataCollection.technicalData.cookies.description')}
              </li>
            </ul>

            <p className="mb-4 font-semibold">
              {t('privacy.dataCollection.otherSources.title')}
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                {t('privacy.dataCollection.otherSources.trustedPartners')}
              </li>
              <li>
                {t('privacy.dataCollection.otherSources.publicSources')}
              </li>
            </ul>
          </div>

          {/* 2. How We Use Personal Data */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black flex items-center gap-2 mb-4">
              <Shield size={28} /> {t('privacy.dataUse.title')}
            </h2>
            <p className="mb-4">
              {t('privacy.dataUse.description')}
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                {t('privacy.dataUse.purposes.provideServices')}
              </li>
              <li>
                {t('privacy.dataUse.purposes.improveServices')}
              </li>
              <li>
                {t('privacy.dataUse.purposes.communicate')}
              </li>
              <li>
                {t('privacy.dataUse.purposes.preventFraud')}
              </li>
              <li>
                {t('privacy.dataUse.purposes.legalCompliance')}
              </li>
              <li>
                {t('privacy.dataUse.purposes.personalize')}
              </li>
              <li>
                {t('privacy.dataUse.purposes.aggregateData')}
              </li>
            </ul>
            <p className="mt-4 bg-yellow-100 p-4 rounded-lg text-gray-800 font-semibold">
              <strong>{t('privacy.dataUse.aiTraining.title')}</strong>{' '}
              {t('privacy.dataUse.aiTraining.description')}{' '}
              <a
                href="mailto:support@sarvalok.com"
                className="text-black underline"
              >
                support@sarvalok.com
              </a>
              .
            </p>
          </div>

          {/* 3. Disclosure of Personal Data */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
              {t('privacy.disclosure.title')}
            </h2>
            <p className="mb-4">
              {t('privacy.disclosure.description')}
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong className="font-semibold">{t('privacy.disclosure.vendors.title')}</strong>{' '}
                {t('privacy.disclosure.vendors.description')}
              </li>
              <li>
                <strong className="font-semibold">{t('privacy.disclosure.advertising.title')}</strong>{' '}
                {t('privacy.disclosure.advertising.description')}
              </li>
              <li>
                <strong className="font-semibold">{t('privacy.disclosure.businessTransfers.title')}</strong> {t('privacy.disclosure.businessTransfers.description')}
              </li>
              <li>
                <strong className="font-semibold">{t('privacy.disclosure.government.title')}</strong>{' '}
                {t('privacy.disclosure.government.description')}
              </li>
              <li>
                <strong className="font-semibold">{t('privacy.disclosure.affiliates.title')}</strong> {t('privacy.disclosure.affiliates.description')}
              </li>
            </ul>
          </div>

          {/* 4. Retention */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
              {t('privacy.retention.title')}
            </h2>
            <p>
              {t('privacy.retention.description')}
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>{t('privacy.retention.factors.provideServices')}</li>
              <li>{t('privacy.retention.factors.resolveDisputes')}</li>
              <li>{t('privacy.retention.factors.security')}</li>
              <li>{t('privacy.retention.factors.legalCompliance')}</li>
              <li>{t('privacy.retention.factors.dataType')}</li>
            </ul>
          </div>

          {/* 5. Your Rights */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
              {t('privacy.rights.title')}
            </h2>
            <p className="mb-4">
              {t('privacy.rights.description')}
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong className="font-semibold">{t('privacy.rights.access.title')}</strong> {t('privacy.rights.access.description')}
              </li>
              <li>
                <strong className="font-semibold">{t('privacy.rights.rectification.title')}</strong> {t('privacy.rights.rectification.description')}
              </li>
              <li>
                <strong className="font-semibold">{t('privacy.rights.erasure.title')}</strong>{' '}
                {t('privacy.rights.erasure.description')}
              </li>
              <li>
                <strong className="font-semibold">{t('privacy.rights.restriction.title')}</strong>{' '}
                {t('privacy.rights.restriction.description')}
              </li>
              <li>
                <strong className="font-semibold">{t('privacy.rights.withdrawConsent.title')}</strong> {t('privacy.rights.withdrawConsent.description')}
              </li>
              <li>
                <strong className="font-semibold">{t('privacy.rights.privacySettings.title')}</strong> {t('privacy.rights.privacySettings.description')}
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>{t('privacy.rights.privacySettings.features.resetConsent')}</li>
                  <li>{t('privacy.rights.privacySettings.features.changeConsent')}</li>
                  <li>{t('privacy.rights.privacySettings.features.viewBanner')}</li>
                  <li>{t('privacy.rights.privacySettings.features.disableTracking')}</li>
                </ul>
                <p className="mt-2 text-sm text-gray-600">
                  {t('privacy.rights.privacySettings.note')}
                </p>
              </li>
              <li>
                {t('privacy.rights.contact.description')}{' '}
                <a
                  href="mailto:support@sarvalok.com"
                  className="text-black underline"
                >
                  support@sarvalok.com
                </a>
                . {t('privacy.rights.contact.response')}
              </li>
            </ul>
          </div>

          {/* 6. Children */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
              {t('privacy.children.title')}
            </h2>
            <p>
              {t('privacy.children.description')}{' '}
              <a
                href="mailto:support@sarvalok.com"
                className="text-black underline"
              >
                support@sarvalok.com
              </a>
              . {t('privacy.children.descriptionEnd')}
            </p>
          </div>

          {/* 7. Security */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black flex items-center gap-2 mb-4">
              <Shield size={28} /> {t('privacy.security.title')}
            </h2>
            <p>
              {t('privacy.security.description')}
            </p>
          </div>

          {/* 8. Changes */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
              {t('privacy.changes.title')}
            </h2>
            <p>
              {t('privacy.changes.description')}
            </p>
          </div>

          {/* 9. Contact Us */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black flex items-center gap-2 mb-4">
              <Mail size={28} /> {t('privacy.contact.title')}
            </h2>
            <div className="space-y-3">
              <p className="flex items-center gap-2">
                <Mail size={20} className="text-black" />
                <a
                  href="mailto:support@sarvalok.com"
                  className="text-black underline"
                >
                  support@sarvalok.com
                </a>
              </p>
              <p className="flex items-center gap-2">
                <Phone size={20} className="text-black" />
                <a href={isGlobal ? "tel:+16504601492" : "tel:+918125831103"} className="text-blue-600 underline">
                  {isGlobal ? "+1 650 460 1492" : "+91 8125831103"}
                </a>
              </p>
              <p className="flex items-center gap-2">
                <MapPin size={20} className="text-black" />
                <span className="whitespace-pre-line">
                  {t(isGlobal ? 'privacy.contact.address_global' : 'privacy.contact.address').replace(/\\n/g, '\n')}
                </span>
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPage;
